import type { PartnerSyncChecklist } from '../../../store/oneButtonStore';

/** Checklist step keys that can be required per partner (excluding leadSyncAction and lead) */
export type PartnerSyncStepKey = 'leadSystem' | 'utility' | 'contact' | 'titleCheck' | 'creditCheck';

export interface PartnerSyncConfig {
    /** Which steps are required for this partner. Order matches display order. */
    requiredSteps: PartnerSyncStepKey[];
    /** Optional label overrides for steps (e.g. "Credit check (IGS and LightReach)") */
    stepLabels?: Partial<Record<PartnerSyncStepKey, string>>;
}

const DEFAULT_STEP_LABELS: Record<PartnerSyncStepKey, string> = {
    leadSystem: 'Lead System sync',
    utility: 'Utility sync',
    contact: 'Contact sync',
    titleCheck: 'Title check',
    creditCheck: 'Credit check',
};

/** Per-partner sync logic: which checklist steps apply and completion rules */
export const PARTNER_SYNC_CONFIG: Record<string, PartnerSyncConfig> = {
    LightReach: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck', 'creditCheck'],
        stepLabels: { creditCheck: 'Credit check (LightReach)' },
    },
    GoodLeap: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck'],
    },
    IGS: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck', 'creditCheck'],
        stepLabels: { creditCheck: 'Credit Check' },
    },
    Cash: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck'],
    },
    Enfin: {
        requiredSteps: ['utility', 'leadSystem', 'contact', 'titleCheck'],
    },
};

export function getPartnerSyncConfig(partnerName: string): PartnerSyncConfig {
    return (
        PARTNER_SYNC_CONFIG[partnerName] ?? {
            requiredSteps: ['leadSystem', 'utility', 'contact', 'titleCheck', 'creditCheck'],
        }
    );
}

export function getStepLabel(partnerName: string, stepKey: PartnerSyncStepKey): string {
    const config = getPartnerSyncConfig(partnerName);
    return config.stepLabels?.[stepKey] ?? DEFAULT_STEP_LABELS[stepKey];
}

/** Returns true only if all steps required by this partner are done */
export function isPartnerSyncCompleteForConfig(
    partnerName: string,
    status: PartnerSyncChecklist | undefined
): boolean {
    if (!status) return false;
    const config = getPartnerSyncConfig(partnerName);
    for (const key of config.requiredSteps) {
        if (!status[key]) return false;
    }
    return true;
}

/**
 * Quote preflight: lead, contact, utility, and lead system only.
 * Title check and credit check remain visible in Partner Sync but do not block quote generation
 * (matches QuotesContractsCard / FinancingGridCard add-quote preflight).
 */
export function isPartnerSyncCompleteForQuote(status: PartnerSyncChecklist | undefined): boolean {
    if (!status) return false;
    return (
        status.lead === true &&
        status.contact === true &&
        status.utility === true &&
        status.leadSystem === true
    );
}

const normPartnerKey = (name: string | null | undefined) =>
    String(name ?? '')
        .replace(/\s+/g, '')
        .toLowerCase();

/**
 * Quote preflight: GoodLeap, Enfin, and Cash do not need the partner checklist before adding quotes.
 * IGS / LightReach (and other partners) still do.
 */
export function partnerRequiresQuoteSyncChecklist(partnerName: string | null | undefined): boolean {
    const n = normPartnerKey(partnerName);
    if (!n) return false;
    if (n === 'cash' || n.includes('goodleap') || n.includes('enfin')) return false;
    return true;
}

export function anyPartnerRequiresQuoteSyncChecklist(
    partners: Iterable<string | null | undefined>,
): boolean {
    for (const partner of partners) {
        if (partnerRequiresQuoteSyncChecklist(partner)) return true;
    }
    return false;
}

/**
 * LightReach eligible products API is available once utility, system design (lead system), and lead
 * are synced — same gates as Trinity before showing eligible products (credit/title not required).
 */
export function isLightreachEligibleProductsPrerequisiteMet(
    partnerName: string,
    status: PartnerSyncChecklist | undefined
): boolean {
    if (!normPartnerKey(partnerName).includes('lightreach')) return false;
    if (!status) return false;
    return status.utility === true && status.leadSystem === true && status.lead === true;
}
